import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import ReferralsFloorRow, { referralHeat } from "./ReferralsFloorRow";

const SWIPE_THRESHOLD = 52;

export default function ReferralsFloorIndex({
  referrals,
  selectedId,
  onSelect,
  desk,
  pagination,
  loading,
  onPageChange,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });
  const [slideDir, setSlideDir] = useState(null);

  const { page = 1, totalPages = 1, total = 0, limit = 15 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + referrals.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  useEffect(() => {
    if (!slideDir) return undefined;
    const timer = window.setTimeout(() => setSlideDir(null), 220);
    return () => window.clearTimeout(timer);
  }, [slideDir, page]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const goPage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setSlideDir(nextPage > page ? "next" : "prev");
    onPageChange(nextPage);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (totalPages <= 1) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goPage(page + 1);
    else goPage(page - 1);
  };

  return (
    <aside className="amf-index arf-index" aria-label="Referral ledger">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Referral ledger</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>{referrals.length}</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
            {desk === "ready" ? " · queue" : ""}
          </span>
        </div>
      </div>

      <div
        ref={listRef}
        className={`amf-index-list arf-index-list${slideDir ? ` arf-index-list-${slideDir}` : ""}${loading ? " arf-index-list-loading" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {loading && referrals.length === 0 ? (
          <div className="arf-index-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading page…</span>
          </div>
        ) : referrals.length === 0 ? (
          <div className="amf-index-empty">No referrals match current filters.</div>
        ) : (
          referrals.map((referral, idx) => {
            const id = referral.id;
            const heatPct = referralHeat(referral);
            return (
              <div key={id} ref={id === selectedId ? selectedRef : null}>
                <ReferralsFloorRow
                  referral={referral}
                  rank={startIndex + idx + 1}
                  selected={id === selectedId}
                  onSelect={() => onSelect(id)}
                  heatPct={heatPct}
                />
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 ? (
        <div className="arf-index-foot">
          <span className="arf-index-foot-meta">Swipe list or use arrows</span>
          <div className="arf-index-pager">
            <button
              type="button"
              className="arf-pager-btn"
              disabled={!canPrev || loading}
              onClick={() => goPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="arf-pager-label">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="arf-pager-btn"
              disabled={!canNext || loading}
              onClick={() => goPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
